import { API } from '../api.js';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LangContext = createContext(null);

// Flatten nested object to dot-key map for t() lookup
function flatten(obj, prefix = '') {
  const result = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
      Object.assign(result, flatten(v, key));
    } else {
      result[key] = String(v);
    }
  }
  return result;
}

export function LangProvider({ children }) {
  const [lang, setLang]         = useState(() => localStorage.getItem('lh_lang') || 'en');
  const [translations, setTrans] = useState({});
  const [languages, setLanguages] = useState([
    { code: 'en', flag: '🇺🇸', name: 'English', is_active: true },
    { code: 'th', flag: '🇹🇭', name: 'Thai', is_active: true }
  ]);

  const loadLanguages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/i18n/languages`);
      if (res.ok) {
        const data = await res.json();
        setLanguages(data);
      }
    } catch (e) {
      console.error('Failed to load languages:', e);
    }
  }, []);

  const loadLang = useCallback(async (l) => {
    try {
      const res  = await fetch(`${API}/api/i18n/${l}`);
      const data = await res.json();
      setTrans(flatten(data));
    } catch {
      // fallback: empty
    }
  }, []);

  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    localStorage.setItem('lh_lang', lang);
    loadLang(lang);
  }, [lang, loadLang]);

  function t(key, fallback) {
    return translations[key] ?? fallback ?? key;
  }

  function switchLang(l) { setLang(l); }

  return (
    <LangContext.Provider value={{
      lang,
      t,
      switchLang,
      languages: languages.filter(l => l.is_active),
      allLanguages: languages,
      reload: () => loadLang(lang),
      reloadLanguages: loadLanguages
    }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() { return useContext(LangContext); }
